// Главная страница `/`.
// - Authenticated → Dashboard (KPI + Streak + Recommendations + Recent materials)
// - Guest        → Public landing (hero + features + pricing preview + FAQ)
// См. docs/ui-spec/{dashboard.authenticated,public.landing}.{desktop,mobile}.md
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AuthenticatedDashboard } from "@/components/dashboard/authenticated-dashboard";
import { DashboardSkeleton } from "@/components/dashboard/dashboard-skeleton";
import { GuestLanding } from "@/components/public/guest-landing";
import { FAQ_KEYS } from "@/components/public/landing/faq-section";
import { JsonLd } from "@/components/seo/JsonLd";
import { NoIndexMeta } from "@/components/seo/NoIndexMeta";
import { PageMeta } from "@/components/seo/PageMeta";
import { useSession } from "@/hooks/use-session";

export const Route = createFileRoute("/")({
	component: HomePage,
	pendingComponent: HomePending,
});

// Скелет домашней страницы при загрузке JS-чанка роута. Сам дашборд дополнительно
// держит этот же скелетон, пока не готовы данные текущего юзера (см. DashboardSkeleton
// + гейт isInitialLoading в authenticated-dashboard).
function HomePending(): React.ReactElement {
	return <DashboardSkeleton />;
}

function HomePage(): React.ReactElement {
	const { t } = useTranslation("landing");
	// Личность известна из кэша (persist store / query cache) → дашборд монтируем
	// СРАЗУ, не дожидаясь ответа auth.me: он идёт фоном и вмешается, только если
	// сервер скажет «гость». Ожидание подтверждения стоило 9.6 с на каждом
	// реоткрытии — см. `isSessionGatePending` и `cold-start-gate.test.ts`.
	//
	// Скелетон здесь больше не нужен: `isSessionPending` истинен только когда о
	// пользователе не известно ничего, а тогда `isAuthenticated` ложен и мы
	// показываем гостевой лендинг (публичная страница обязана рисоваться сразу).
	const { t: tc } = useTranslation("common");
	const { isAuthenticated } = useSession();

	// FAQPage и HowTo собираются из тех же ключей, что рисует страница: отдельных
	// SEO-текстов у них нет. Иначе разметка и видимый текст разъедутся при первой
	// же правке копирайта — а Google расценивает такое расхождение как обман.
	const faqSchema = useMemo(
		() => ({
			"@type": "FAQPage",
			mainEntity: FAQ_KEYS.map((key) => ({
				"@type": "Question",
				name: t(`faq.items.${key}.q`),
				acceptedAnswer: { "@type": "Answer", text: t(`faq.items.${key}.a`) },
			})),
		}),
		[t],
	);

	const howToSchema = useMemo(
		() => ({
			"@type": "HowTo",
			name: t("method.title"),
			description: t("method.lead"),
			step: (["step1", "step2", "step3"] as const).map((key, i) => ({
				"@type": "HowToStep",
				position: i + 1,
				name: tc(`method.${key}.label`),
				text: t(`method.${key}.body`),
				url: "https://study.iec.exchange/#method",
			})),
		}),
		[t, tc],
	);

	if (isAuthenticated) {
		return (
			<>
				<NoIndexMeta />
				<AuthenticatedDashboard />
			</>
		);
	}
	return (
		<>
			<PageMeta title={t("seo.title")} description={t("seo.description")} path="/" noTitleSuffix />
			<JsonLd
				id="organization"
				schema={{
					"@type": "Organization",
					name: "StudyHub",
					url: "https://study.iec.exchange",
					logo: "https://study.iec.exchange/icon-512.png",
					sameAs: ["https://iec.exchange"],
				}}
			/>
			<JsonLd
				id="website"
				schema={{
					"@type": "WebSite",
					name: "StudyHub",
					url: "https://study.iec.exchange",
					description: t("seo.descriptionShort"),
					potentialAction: {
						"@type": "SearchAction",
						target: "https://study.iec.exchange/search?q={search_term_string}",
						"query-input": "required name=search_term_string",
					},
					inLanguage: ["ru", "en"],
				}}
			/>
			<JsonLd
				id="softwareapp"
				schema={{
					"@type": "SoftwareApplication",
					name: "StudyHub",
					applicationCategory: "EducationApplication",
					operatingSystem: "Web, iOS, Android",
					offers: {
						"@type": "Offer",
						price: "0",
						priceCurrency: "USD",
					},
				}}
			/>
			<JsonLd id="faq" schema={faqSchema} />
			<JsonLd id="howto" schema={howToSchema} />
			<GuestLanding />
		</>
	);
}
